import { Effect, Data, Schedule } from 'effect';
import * as Sentry from '@sentry/browser';
import { ApiService } from '@/domain/apiService';
import { WebPubSubClient, OnServerDataMessageArgs, OnGroupDataMessageArgs } from '@azure/web-pubsub-client';
import { WSConnectionError } from '@/domain/ws/errors';

interface WSClientOptions {
  onMessage: (data: any) => void;
  context: any;
  cb?: () => void;
}

class WSCloseError extends Data.TaggedError('WSCloseError')<{
  readonly message: string;
  readonly originalError: unknown;
}> {}

const JSONMessageDeserializer = (message: any) => {
  try {
    return JSON.parse(message);
  } catch (e) {
    return {};
  }
};

const makePubSubWSClient = (options: WSClientOptions) =>
  Effect.gen(function* () {
    const { onMessage, context } = options;

    const api = yield* ApiService;

    // The Azure client awaits getClientAccessUrl() during start(). If this token
    // fetch throws, start() rejects and the client is left Stopped — autoReconnect
    // does NOT cover a failed initial handshake, so the connection is lost with no
    // retry. Wrap the fetch in a bounded Effect retry (exponential backoff, capped
    // total attempts) so transient failures are retried before we give up. From
    // the library's point of view the promise simply takes a little longer.
    const fetchConnectionUrl = Effect.tryPromise(() => api.getWSConnectionUrl({ data: context })).pipe(
      Effect.map((response) => response.data.connection_url),
      Effect.retry({
        // 2 retries (3 attempts total) with exponential backoff (500ms, 1s, …),
        // capped at 5s per delay. `times` bounds the count; `schedule` the delay.
        times: 2,
        schedule: Schedule.exponential('500 millis').pipe(Schedule.upTo('5 seconds')),
      }),
    );

    const ws = new WebPubSubClient(
      {
        getClientAccessUrl: async (opts) => {
          const abortSignal = opts?.abortSignal as AbortSignal | undefined;
          // Respect the library's abort (e.g. navigation cancelled the connect):
          // fail fast instead of retrying a fetch that is no longer wanted. Use an
          // AbortError (name checked in the catch) so this normal-cleanup path is
          // NOT reported to Sentry as a connection failure.
          if (abortSignal?.aborted) {
            throw new DOMException('WS connection aborted before token fetch', 'AbortError');
          }
          return Effect.runPromise(fetchConnectionUrl, { signal: abortSignal });
        },
      },
      {
        autoReconnect: true,
      },
    );

    const start = Effect.tryPromise({
      try: () => ws.start(),
      catch: (error) =>
        new WSConnectionError({
          message: `Failed to establish WebSocket connection: ${error instanceof Error ? error.message : error}`,
          originalError: error,
        }),
    });

    const stop = Effect.try({
      try: () => {
        if ((ws as any)._wsClient?.isOpen?.()) {
          ws.stop();
        } else {
          // We were asked to close the socket before it finished connecting.
          // This is the fast-navigation race we are tracking — log it to Sentry
          // so we can see how often it happens in production.
          console.warn('Trying to stop WS that has not connected yet — entering setTimeout poll');
          Sentry.captureMessage('Attempting to stop WS that has not connected yet', {
            level: 'warning',
            tags: { source: 'WS_STOP_BEFORE_CONNECTED' },
            extra: {
              userContext: (context as any)?.user_context,
            },
          });
          const checkAndStop = () => {
            if ((ws as any)._wsClient?.isOpen?.()) {
              ws.stop();
            } else {
              setTimeout(checkAndStop, 100);
            }
          };
          setTimeout(checkAndStop, 100);
        }
      },
      catch: (error) =>
        new WSCloseError({
          message: `Failed to close WebSocket connection: ${error instanceof Error ? error.message : error}`,
          originalError: error,
        }),
    });

    return { ws, start, stop };
  });

// An abort is normal navigation/cleanup — not an actionable failure.
const isAbort = (error: WSConnectionError) => {
  const cause = error.originalError;
  return cause instanceof DOMException
    ? cause.name === 'AbortError'
    : cause instanceof Error && cause.name === 'AbortError';
};

// Reporting is left to each consumer: a socket started once reports every failed start, a channel that
// retries for the whole session reports an outage once.
export const reportWSConnectionError = (error: WSConnectionError, context: unknown) =>
  Effect.sync(() => {
    if (isAbort(error)) return;
    // Effect captures this failure internally so it never reaches Sentry's default error handler —
    // report it explicitly. Capture the tagged WSConnectionError (a real Error via Data.TaggedError)
    // so Sentry keeps consistent grouping/stack; the raw cause is preserved in `extra`.
    Sentry.captureException(error, {
      level: 'error',
      tags: { source: 'WS_CONNECTION_ERROR' },
      extra: {
        message: error.message,
        originalError: error.originalError,
        userContext: (context as any)?.user_context,
      },
    });
  });

export class WSClientService extends Effect.Service<WSClientService>()('WSClient', {
  effect: makePubSubWSClient,
  dependencies: [ApiService.make], // Service Layer (implementation)
}) {}

export const createWSClientConnection = (options: WSClientOptions) =>
  Effect.acquireUseRelease(
    // creating resource
    Effect.gen(function* () {
      const wsClient = yield* WSClientService;
      yield* wsClient.start;
      // return client itself to later disconnect it once user leaves the page
      return wsClient;
    })
      .pipe(Effect.provide(WSClientService.Default(options)))
      .pipe(
        Effect.tapError((error) =>
          Effect.sync(() => console.error('Connection Error:', error.message, error.originalError)),
        ),
      ),
    // using resource
    (client) => {
      const { onMessage } = options;

      const handleMessage = (event: OnServerDataMessageArgs | OnGroupDataMessageArgs) => {
        onMessage(JSONMessageDeserializer(event.message.data));
      };
      client.ws.on('group-message', handleMessage);
      // not sure we receive this type of message
      client.ws.on('server-message', handleMessage);

      return Effect.never; // Keep effect running until interrupted
    },
    // releasing resource
    (client) =>
      client.stop.pipe(
        Effect.catchTag('WSCloseError', (error) => {
          console.error('WS Close Error:', error.message, error.originalError);
          return Effect.void;
        }),
      ),
  );

export const createScopedWebSocketEffect = (options: WSClientOptions) =>
  Effect.scoped(createWSClientConnection(options));
